class HHStickyAddToCart extends HTMLElement {
  constructor() {
    super()
    this.syncing = false
    this.onVariantChanged = this.onVariantChanged.bind(this)
    this.onStickySizeChange = this.onStickySizeChange.bind(this)
    this.onButtonClick = this.onButtonClick.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)
    this.onOverlayClick = this.onOverlayClick.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.updatePagePadding = this.updatePagePadding.bind(this)
  }

  connectedCallback() {
    this.sectionId = this.dataset.section
    this.formId = this.dataset.formId
    this.hasSelectableSize = this.dataset.hasSelectableSize === 'true'

    this.productForm = document.getElementById(this.formId)
    this.button = this.querySelector('[data-sticky-atc-button]')
    this.sizePanel = this.querySelector('[data-sticky-size-panel]')
    this.sizeInputs = this.querySelectorAll('[data-sticky-size-input]')
    this.closeButton = this.querySelector('[data-sticky-close]')
    this.focusTrap = this.querySelector('[data-sticky-focus-trap]')
    this.overlay = this.querySelector('[data-sticky-overlay]')
    this.stickyBar = this.querySelector('.sticky-atc__bar')

    this.button?.addEventListener('click', this.onButtonClick)
    this.closeButton?.addEventListener('click', this.onCloseClick)
    this.overlay?.addEventListener('click', this.onOverlayClick)
    this.addEventListener('change', this.onStickySizeChange)
    document.addEventListener('variant:changed', this.onVariantChanged)

    this.schedulePagePaddingUpdate()
    this.initSizeChartObserver()
    this.initResizeObserver()
    this.initVariantPickerSync()
    this.initSizekickClone()
  }

  initResizeObserver() {
    this.resizeObserver = new ResizeObserver(this.updatePagePadding)
    this.resizeObserver.observe(this)

    if (this.stickyBar) {
      this.resizeObserver.observe(this.stickyBar)
    }

    const stickyButton = this.stickyBar?.querySelector('hh-button-wrapper, button')
    if (stickyButton) {
      this.resizeObserver.observe(stickyButton)
    }

    this.onResize = this.updatePagePadding.bind(this)
    window.addEventListener('resize', this.onResize)

    if (document.fonts?.ready) {
      document.fonts.ready.then(this.updatePagePadding)
    }

    customElements.whenDefined('hh-button-wrapper').then(this.updatePagePadding)
  }

  initVariantPickerSync() {
    const sync = () => {
      this.variantPicker = document.querySelector(
        `hh-variant-picker[data-section="${this.sectionId}"]`
      )

      if (!this.isVariantPickerReady()) return

      this.syncFromVariantPicker()
    }

    if (customElements.get('hh-variant-picker')) {
      sync()
      return
    }

    customElements.whenDefined('hh-variant-picker').then(sync)
  }

  isVariantPickerReady() {
    return typeof this.variantPicker?.getSelectedOptionValue === 'function'
  }

  initSizekickClone() {
    this.sizekickTarget = this.querySelector('[data-sticky-sizekick]')
    this.sizekickSource = document.getElementById('sizekickWidget')

    if (!this.sizekickTarget || !this.sizekickSource) return

    this.syncSizekickClone = this.syncSizekickClone.bind(this)
    this.onSizekickCloneClick = this.onSizekickCloneClick.bind(this)

    this.syncSizekickClone()

    if (this.sizekickTarget.hidden) {
      this.sizekickObserver = new MutationObserver(this.syncSizekickClone)
      this.sizekickObserver.observe(this.sizekickSource, {
        childList: true,
        subtree: true,
      })
    }
  }

  syncSizekickClone() {
    if (!this.sizekickTarget || !this.sizekickSource?.firstElementChild) return

    const sourceWidget = this.sizekickSource.firstElementChild
    const existingClone = this.sizekickTarget.firstElementChild

    if (existingClone && existingClone.outerHTML === sourceWidget.outerHTML) {
      this.sizekickTarget.hidden = false
      this.sizekickObserver?.disconnect()
      this.sizekickObserver = null
      return
    }

    this.sizekickTarget.replaceChildren(sourceWidget.cloneNode(true))
    this.sizekickTarget.hidden = false
    this.sizekickTarget.removeEventListener('click', this.onSizekickCloneClick)
    this.sizekickTarget.addEventListener('click', this.onSizekickCloneClick)
    this.sizekickObserver?.disconnect()
    this.sizekickObserver = null
  }

  onSizekickCloneClick(event) {
    const sourceWidget = this.sizekickSource?.firstElementChild
    if (!sourceWidget) return

    event.preventDefault()

    const sourceClickTarget =
      sourceWidget.matches('button, a, [role="button"]')
        ? sourceWidget
        : sourceWidget.querySelector('button, a, [role="button"]')

    sourceClickTarget?.click()
  }

  isStickyVisible() {
    return window.getComputedStyle(this).display !== 'none'
  }

  getStickyHeight() {
    if (!this.isStickyVisible()) return 0

    const bar = this.stickyBar ?? this.querySelector('.sticky-atc__bar')
    if (!bar) return 0

    const barHeight = bar.getBoundingClientRect().height || bar.offsetHeight
    const safeArea = parseFloat(getComputedStyle(this).paddingBottom) || 0

    return barHeight + safeArea
  }

  schedulePagePaddingUpdate() {
    this.updatePagePadding()

    requestAnimationFrame(() => {
      this.updatePagePadding()
      requestAnimationFrame(() => this.updatePagePadding())
    })
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onButtonClick)
    this.closeButton?.removeEventListener('click', this.onCloseClick)
    this.overlay?.removeEventListener('click', this.onOverlayClick)
    this.removeEventListener('change', this.onStickySizeChange)
    document.removeEventListener('variant:changed', this.onVariantChanged)
    document.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.onResize)
    this.sizeChartObserver?.disconnect()
    this.sizekickObserver?.disconnect()
    this.sizekickTarget?.removeEventListener('click', this.onSizekickCloneClick)
    this.resizeObserver?.disconnect()
    this.unlockScroll()

    if (!document.querySelector('hh-sticky-add-to-cart')) {
      document.documentElement.style.removeProperty('--hh-sticky-atc-height')
    }
  }

  get addToCartText() {
    return this.dataset.finalSale === 'true'
      ? window.variantStrings.addToCartFinalSale
      : window.variantStrings.addToCart
  }

  isPanelOpen() {
    return this.classList.contains('is-open')
  }

  lockScroll() {
    document.documentElement.classList.add('overflow-hidden')
  }

  unlockScroll() {
    const hasOpenStickyPanel = document.querySelector(
      'hh-sticky-add-to-cart.is-open'
    )
    if (!hasOpenStickyPanel) {
      document.documentElement.classList.remove('overflow-hidden')
    }
  }

  resetButtonForNoVariant() {
    if (!this.button) return
    this.button.removeAttribute('disabled')
    this.button.textContent = window.variantStrings.selectSize
  }

  setButtonForOpenPanel() {
    this.variantPicker
      ?.getCartData()
      .then(() => this.variantPicker?.refreshStickyButton?.())
  }

  onCloseClick() {
    this.closeSizePanel()
  }

  onOverlayClick() {
    this.closeSizePanel()
  }

  onKeyDown(event) {
    if (event.code.toUpperCase() !== 'ESCAPE' || !this.isPanelOpen()) return

    event.preventDefault()
    this.closeSizePanel()
  }

  showOverlay() {
    if (!this.overlay) return

    requestAnimationFrame(() => {
      this.overlay.classList.add('is-visible')
    })
  }

  hideOverlay() {
    this.overlay?.classList.remove('is-visible')
  }

  activateFocusTrap() {
    this.focusTrap?.setAttribute('active', '')
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('focus-trap:refresh'))
    })
  }

  deactivateFocusTrap() {
    this.focusTrap?.setAttribute('active', 'false')
  }

  initSizeChartObserver() {
    this.sizeChartModal = document.querySelector(
      'hh-size-chart-modal#js-size-chart-trigger'
    )
    if (!this.sizeChartModal) return

    this.sizeChartObserver = new MutationObserver(() => {
      if (!this.isPanelOpen()) return

      if (this.sizeChartModal.hasAttribute('open')) {
        this.deactivateFocusTrap()
      } else {
        this.activateFocusTrap()
        this.lockScroll()
      }
    })

    this.sizeChartObserver.observe(this.sizeChartModal, {
      attributes: true,
      attributeFilter: ['open'],
    })
  }

  restoreFocus() {
    if (this.previousFocusElement?.focus) {
      this.previousFocusElement.focus({ focusVisible: false })
      return
    }

    this.button?.focus({ focusVisible: false })
  }

  focusAddButtonWhenReady(attempts = 0) {
    if (!this.button?.hasAttribute('disabled')) {
      this.button?.focus({ focusVisible: false })
      return
    }

    if (attempts < 10) {
      window.setTimeout(() => this.focusAddButtonWhenReady(attempts + 1), 50)
    }
  }

  get currentVariant() {
    return this.variantPicker?.currentVariant ?? null
  }

  onVariantChanged(event) {
    if (event.detail.sectionId !== this.sectionId) return
    this.syncFromVariantPicker(event.detail)
  }

  syncFromVariantPicker(detail = null) {
    if (!this.isVariantPickerReady()) return

    const variant = detail?.variant ?? this.currentVariant
    const sizeValue =
      this.variantPicker?.getSelectedOptionValue('Size') ??
      this.variantPicker?.getSelectedOptionValue('size')

    if (sizeValue && this.sizeInputs.length) {
      this.syncing = true
      this.sizeInputs.forEach((input) => {
        input.checked = input.value === sizeValue
      })
      this.syncing = false
    }

    if (this.isPanelOpen()) {
      this.variantPicker
        ?.getCartData()
        .then(() => this.variantPicker?.refreshStickyButton?.())
    } else if (variant) {
      this.focusAddButtonWhenReady()
    }

    this.syncLabelStyles()
  }

  syncLabelStyles() {
    if (!this.isVariantPickerReady()) return

    this.variantPicker.querySelectorAll('.js-variant-label').forEach((mainLabel) => {
      const optionValue = mainLabel.dataset.optionValue
      if (!optionValue) return

      const stickyLabel = this.querySelector(
        `.js-sticky-variant-label[data-option-value="${CSS.escape(optionValue)}"]`
      )
      if (!stickyLabel) return

      stickyLabel.className = mainLabel.className
      if (!stickyLabel.classList.contains('js-sticky-variant-label')) {
        stickyLabel.classList.add('js-sticky-variant-label')
      }
    })
  }

  onStickySizeChange(event) {
    if (this.syncing || !event.target.matches('[data-sticky-size-input]')) {
      return
    }

    if (typeof this.variantPicker?.selectOptionByValue !== 'function') return

    const optionName =
      event.target.dataset.optionName || event.target.name.replace(/^sticky-/, '')
    this.variantPicker?.selectOptionByValue(optionName, event.target.value)
  }

  onButtonClick() {
    if (this.button?.dataset.action === 'notify-me') {
      document.querySelector('#BIS_trigger')?.click()
      return
    }

    if (this.button?.hasAttribute('disabled')) return

    if (!this.currentVariant) {
      if (this.hasSelectableSize && !this.isPanelOpen()) {
        this.openSizePanel()
        return
      }

      const variantId = this.productForm?.querySelector('input[name="id"]')?.value
      if (!variantId) return
    }

    this.submitForm()
  }

  submitForm() {
    const variantId = this.productForm?.querySelector('input[name="id"]')?.value
    if (!variantId) return

    if (this.isPanelOpen()) {
      this.closeSizePanel({ restoreFocus: false })
    }

    this.productForm.requestSubmit()
  }

  openSizePanel() {
    if (!this.sizePanel) return

    this.previousFocusElement = document.activeElement
    this.classList.add('is-open')
    this.sizePanel.setAttribute('aria-hidden', 'false')
    this.setPanelHeight()
    this.updatePagePadding()
    this.setButtonForOpenPanel()
    this.lockScroll()
    this.showOverlay()
    this.activateFocusTrap()
    document.addEventListener('keydown', this.onKeyDown)
  }

  closeSizePanel({ restoreFocus = true } = {}) {
    if (!this.sizePanel) return
    const wasOpen = this.isPanelOpen()

    this.classList.remove('is-open')
    this.sizePanel.setAttribute('aria-hidden', 'true')
    this.hideOverlay()
    this.deactivateFocusTrap()
    document.removeEventListener('keydown', this.onKeyDown)
    this.sizePanel.style.removeProperty('--sticky-atc-panel-height')
    this.updatePagePadding()
    this.unlockScroll()

    if (wasOpen && !this.currentVariant) {
      this.resetButtonForNoVariant()
    }

    if (wasOpen && restoreFocus) {
      this.restoreFocus()
    }
  }

  setPanelHeight() {
    if (!this.sizePanel || !this.isPanelOpen()) return

    this.sizePanel.style.maxHeight = 'none'
    const contentHeight = this.sizePanel.scrollHeight
    this.sizePanel.style.removeProperty('maxHeight')

    const maxAllowed = Math.min(contentHeight, window.innerHeight * 0.55)
    this.sizePanel.style.setProperty(
      '--sticky-atc-panel-height',
      `${maxAllowed}px`
    )
  }

  updatePagePadding() {
    if (this.isPanelOpen()) {
      this.setPanelHeight()
    }

    document.documentElement.style.setProperty(
      '--hh-sticky-atc-height',
      `${this.getStickyHeight()}px`
    )
  }
}

customElements.define('hh-sticky-add-to-cart', HHStickyAddToCart)

function refreshStickyAtcHeights() {
  document.querySelectorAll('hh-sticky-add-to-cart').forEach((sticky) => {
    sticky.updatePagePadding?.()
  })
}

refreshStickyAtcHeights()
requestAnimationFrame(refreshStickyAtcHeights)
